import { api } from "./client";
import type { ApiResponse, PaginatedResponse } from "./types";

// Mirrors apps/api/src/modules/questions/questions.service.ts return maps.
// List envelope: sendSuccess(res, { questions, meta }) → meta is NESTED in data.

export interface QnaUser {
  id: string;
  name: string;
  avatarUrl: string | null;
}

export interface CategoryRef {
  code: string;
  name: string;
  icon?: string | null;
}

export type QuestionScope = "city" | "metro" | "state" | "identity" | "global";

export interface MetroRef {
  code: string;
  name: string;
}

export interface QuestionTopic {
  code: string;
  name: string;
  icon: string | null;
  sortOrder: number;
  isActive: boolean;
  /** False means a question on this topic must not name a trade. */
  allowsProviderCategory: boolean;
}

/** Why a question reached the reader. Names a KIND of match, never a value. */
export interface Relevance {
  primary: string;
  reasons: string[];
  label: string;
}

export interface QuestionListItem {
  id: string;
  title: string;
  body: string; // truncated to ~200 chars in list
  /** What it is ABOUT. */
  topic: CategoryRef | null;
  /** The trade that can answer it. `category` is the deprecated alias. */
  providerCategory: CategoryRef | null;
  category: CategoryRef | null;
  scope: QuestionScope;
  city: string | null;
  state: string | null;
  metro: MetroRef | null;
  originState: string | null;
  tags: string[];
  answerCount: number;
  viewCount: number;
  /** The asker accepted an answer. `isAnswered` is the deprecated alias. */
  isResolved: boolean;
  isAnswered: boolean;
  acceptedAnswerId: string | null;
  resolvedAt: string | null;
  user: QnaUser | null;
  isAnonymous: boolean;
  imageUrl: string | null;
  videoUrl: string | null;
  /** Only for an authenticated caller. */
  relevance?: Relevance;
  createdAt: string;
  updatedAt: string;
}

export interface Answer {
  id: string;
  body: string;
  bodyHtml: string | null;
  upvoteCount: number;
  isAccepted: boolean;
  /** Null for an answer posted as a business. */
  user: QnaUser | null;
  /** The business the answer was posted as; null for a member's answer. */
  business: { id: string; name: string; slug: string; categoryCode: string } | null;
  mentionedProviders: {
    id: string;
    name: string;
    slug: string;
    categoryCode: string;
    city: string;
    avgRating: number;
    vouchCount: number;
  }[];
  hasUpvoted: boolean;
  createdAt: string;
}

export interface QuestionDetail extends Omit<QuestionListItem, "body"> {
  body: string; // full body
  isOwner: boolean;
  answers: Answer[];
}

export interface ListQuestionsParams {
  q?: string;
  category?: string;
  topic?: string;
  scope?: QuestionScope;
  metro?: string;
  city?: string;
  state?: string;
  tags?: string;
  answered?: boolean;
  myWorldOnly?: boolean;
  sort?: "relevant" | "created_at" | "answer_count" | "view_count";
  page?: number;
  limit?: number;
}

export function listQuestions(params?: ListQuestionsParams) {
  return api.get<
    ApiResponse<{
      questions: QuestionListItem[];
      meta: PaginatedResponse<unknown>["meta"];
    }>
  >("/questions", params as Record<string, string | number | boolean | undefined>);
}

export function getQuestion(questionId: string) {
  return api.get<ApiResponse<QuestionDetail>>(`/questions/${questionId}`);
}

export interface CreateQuestionInput {
  title: string; // 10–200
  body: string; // 20–5000
  /** The clear name. `categoryCode` still works and this one wins. */
  providerCategoryCode?: string;
  categoryCode?: string;
  topicCode?: string;
  scope?: QuestionScope;
  city?: string;
  state?: string;
  originState?: string;
  tags?: string[];
  isAnonymous?: boolean;
}

/** Whether anybody was actually told. A quiet question has three causes. */
export interface Delivery {
  status: "queued" | "no_audience" | "failed";
  scope: QuestionScope;
  audienceBand: "none" | "small" | "medium" | "large";
}

export function createQuestion(input: CreateQuestionInput) {
  return api.post<ApiResponse<QuestionListItem & { delivery: Delivery }>>(
    "/questions",
    input,
  );
}

/** What a question can be filed under. NOT the provider directory. */
export function listQuestionTopics() {
  return api.get<ApiResponse<{ topics: QuestionTopic[] }>>("/questions/topics");
}

export interface QuestionSummary {
  openNeeds: number;
  withAnswers: number;
  resolved: number;
  medianFirstAnswerMinutes: number | null;
}

/** Community totals. Same for page 1 and page 2 — never derived from a page. */
export function getQuestionsSummary(params?: ListQuestionsParams) {
  return api.get<ApiResponse<QuestionSummary>>(
    "/questions/summary",
    params as Record<string, string | number | boolean | undefined>,
  );
}

export interface QuestionMatch extends QuestionListItem {
  matchReason: string;
}

/** Advisory duplicate check. Never blocks a post. */
export function findQuestionMatches(input: {
  title: string;
  body?: string;
  topicCode?: string;
  scope?: QuestionScope;
  city?: string;
}) {
  return api.post<ApiResponse<{ matches: QuestionMatch[] }>>(
    "/questions/matches",
    input,
  );
}

export function createAnswer(questionId: string, body: string) {
  return api.post<ApiResponse<Answer>>(`/questions/${questionId}/answers`, {
    body,
  });
}

// NOTE: upvote/accept are top-level under /questions/answers/:answerId/*
// (NOT nested under the question id).
export function toggleUpvote(answerId: string) {
  return api.post<ApiResponse<{ upvoteCount: number; hasUpvoted: boolean }>>(
    `/questions/answers/${answerId}/upvote`,
  );
}

export function acceptAnswer(answerId: string) {
  return api.post<
    ApiResponse<{
      accepted: true;
      questionId: string;
      acceptedAnswerId: string;
      isResolved: true;
      resolvedAt: string;
    }>
  >(`/questions/answers/${answerId}/accept`);
}
